/**
 * The rollout demo's script: four simulated servers, five playbooks, and the order things happen in.
 *
 * ## Every step is the product's own behaviour
 *
 * The same rule as the rest of the landing page: every claim checkable against the code. A run
 * checks every server before it changes any of them (RunCoordinator), signs in over SSH against a
 * pinned host key, and confirms the system is supported and root is available (TargetWorker). One
 * server goes first, and the run **waits for a person** before the rest; the rest go in batches,
 * stopping at the first failure (RunStrategy). Every server's output and exit code is kept in run
 * history. The sentences at those moments are the ones the run page itself writes.
 *
 * What each playbook reports is what its script reports: Update Packages never reboots and says
 * when a reboot is needed; Clean Disk Space trims the journal to 500 MB and deletes rotated logs
 * older than 30 days (its defaults) and never touches home directories, websites, databases or
 * Docker volumes; the hardening playbook arms an undo before its first change and proves SSH still
 * works before it disarms it; Restart Services tests a configuration before it restarts anything.
 *
 * ## The numbers are an example, and the page says so
 *
 * Package counts, freed space and disk figures are illustrative, inside a panel labelled as a
 * simulation, and they add up: the disk percentages follow from the space freed. Nothing here is a
 * measurement or a performance claim, and time is compressed.
 *
 * ## Kept free of React
 *
 * The script is data and the component plays it, so changing what the demo says never means
 * touching how it animates.
 */

export type ServerId = 'web' | 'db' | 'app' | 'worker';

export interface DemoServer {
  id: ServerId;
  name: string;
  os: string;
  packageManager: 'apt' | 'dnf';
  /** How the platform acts as root there: the preflight's "Root access" check. */
  root: 'sudo' | 'root';
  /** Disk used, in percent, before the run. */
  disk: number;
}

export const SERVERS: DemoServer[] = [
  { id: 'web', name: 'Ubuntu-Web-01', os: 'Ubuntu 24.04', packageManager: 'apt', root: 'sudo', disk: 72 },
  { id: 'db', name: 'Debian-DB-01', os: 'Debian 12', packageManager: 'apt', root: 'root', disk: 84 },
  { id: 'app', name: 'Rocky-App-02', os: 'Rocky Linux 9', packageManager: 'dnf', root: 'sudo', disk: 44 },
  { id: 'worker', name: 'Ubuntu-Worker-03', os: 'Ubuntu 22.04', packageManager: 'apt', root: 'sudo', disk: 63 },
];

/** The server that goes first: the first to pass the preflight, as in a real run. */
export const CANARY: ServerId = 'web';

type Rest = Exclude<ServerId, 'web'>;

/** After the canary, two at a time. Any batch size from 1 to 50 is a real choice. */
export const BATCHES: Rest[][] = [['db', 'app'], ['worker']];

export type PlaybookId =
  | 'update-packages'
  | 'install-docker'
  | 'clean-disk-space'
  | 'security-hardening'
  | 'restart-services';

export interface DemoPlaybook {
  id: PlaybookId;
  /** In the picker. */
  label: string;
  /** Its name in the product, which the run output uses. */
  name: string;
  /** One line under the label. */
  summary: string;
  /** The server badge while it works ("Updating"), and the count in the summary ("Updated"). */
  doing: string;
  done: string;
  /** What the canary server does, then what proves it worked. */
  canary: { work: string[]; validate: string[] };
  /** One line for each of the other servers. */
  rollout: Record<Rest, string>;
  /** Disk used afterwards, for the playbook that frees space. */
  diskAfter?: Record<ServerId, number>;
  /** The one finding the summary repeats. */
  note: string;
}

export const PLAYBOOKS: DemoPlaybook[] = [
  {
    id: 'update-packages',
    label: 'Update Packages',
    name: 'Update Packages',
    summary: 'Everything, or security fixes only.',
    doing: 'Updating',
    done: 'Updated',
    canary: {
      work: [
        'Package lists refreshed from the server’s own repositories',
        'Upgraded 14 packages, including openssl and openssh-server',
        'Configuration files changed by hand were kept',
      ],
      validate: ['Nothing was removed to make an upgrade fit', 'No reboot needed on this server'],
    },
    rollout: {
      db: 'Debian-DB-01 · upgraded 11 packages · no reboot needed',
      app: 'Rocky-App-02 · dnf · upgraded 9 packages · no reboot needed',
      worker: 'Ubuntu-Worker-03 · upgraded 16 packages · a new kernel needs a reboot',
    },
    note: 'Ubuntu-Worker-03 needs a reboot to load its new kernel. Techie Gain never reboots a server: the run says so and leaves the timing to you.',
  },
  {
    id: 'install-docker',
    label: 'Install Docker',
    name: 'Install Docker',
    summary: 'Docker Engine and Compose, from Docker.',
    doing: 'Installing',
    done: 'Installed',
    canary: {
      work: [
        'Docker’s release key checked · apt repository configured',
        'Installed Docker Engine, Buildx and the Compose plugin',
        'Docker enabled and started',
      ],
      validate: ['Docker Engine is answering', 'Docker Compose is available'],
    },
    rollout: {
      db: 'Debian-DB-01 · Docker’s apt repository · Docker Engine answering',
      app: 'Rocky-App-02 · Docker’s dnf repository · Docker Engine answering',
      worker: 'Ubuntu-Worker-03 · Docker Engine answering · Compose available',
    },
    note: 'Each server got Docker from Docker’s own repository for its distribution, after the signing key was checked. No containers were started.',
  },
  {
    id: 'clean-disk-space',
    label: 'Clean Disk Space',
    name: 'Clean Disk Space',
    summary: 'Caches, the journal and old logs. Never your data.',
    doing: 'Cleaning',
    done: 'Cleaned',
    canary: {
      work: [
        'Package caches cleared · 610 MB freed',
        'Journal trimmed to 500 MB · 2.9 GB freed',
        'Rotated logs older than 30 days deleted · 1.2 GB freed',
      ],
      validate: [
        'Left alone: home directories, websites, databases, Docker volumes',
        'Free space measured again · disk 72% → 60%',
      ],
    },
    rollout: {
      db: 'Debian-DB-01 · 4.1 GB freed · disk 84% → 79%',
      app: 'Rocky-App-02 · dnf caches cleared · 1.1 GB freed · disk 44% → 41%',
      worker: 'Ubuntu-Worker-03 · 2.2 GB freed · disk 63% → 54%',
    },
    diskAfter: { web: 60, db: 79, app: 41, worker: 54 },
    note: '12.1 GB freed across the four servers, from caches, the journal and old rotated logs. Home directories, websites, databases and Docker volumes were left alone.',
  },
  {
    id: 'security-hardening',
    label: 'Security Hardening',
    name: 'Advanced Security: Fail2Ban & Firewall Hardening',
    summary: 'Firewall and Fail2Ban, without locking you out.',
    doing: 'Hardening',
    done: 'Hardened',
    canary: {
      work: [
        'Undo armed: a failure puts every change back',
        'ufw turned on · SSH and the ports in use (80, 443) allowed',
        'Fail2Ban installed · 5 failed logins in 10 minutes bans for 1 hour',
        'Safer SSH settings applied · sshd -t passed',
      ],
      validate: [
        'A real failed login was caught by the Fail2Ban jail',
        'The original SSH connection is still open · undo disarmed',
      ],
    },
    rollout: {
      db: 'Debian-DB-01 · ufw turned on · Fail2Ban tested · SSH still reachable',
      app: 'Rocky-App-02 · firewalld already in use, rules added · SSH still reachable',
      worker: 'Ubuntu-Worker-03 · ufw turned on · Fail2Ban tested · SSH still reachable',
    },
    note: 'SSH stayed reachable on every server. A failed check on any of them would have put every change back before the step ended.',
  },
  {
    id: 'restart-services',
    label: 'Restart Services',
    name: 'Restart Services',
    summary: 'Tests each configuration before it restarts.',
    doing: 'Restarting',
    done: 'Restarted',
    canary: {
      work: ['Service to restart: sshd', 'sshd: configuration test passed', 'Restarting sshd (it is active)'],
      validate: ['sshd came back: active (running)'],
    },
    rollout: {
      db: 'Debian-DB-01 · configuration test passed · sshd restarted, active',
      app: 'Rocky-App-02 · configuration test passed · sshd restarted, active',
      worker: 'Ubuntu-Worker-03 · configuration test passed · sshd restarted, active',
    },
    note: 'Each configuration was tested before its restart. A failed test stops the run on that server with the service still running.',
  },
];

// ── What the component plays ────────────────────────────────────────────────

export type Stage = 'preflight' | 'canary' | 'rollout';

export type ServerState = 'ready' | 'checking' | 'waiting' | 'canary' | 'working' | 'success' | 'untouched';

export type StageState = 'pending' | 'running' | 'waiting' | 'success' | 'skipped';

export type CheckState = 'pending' | 'running' | 'success' | 'skipped';

/** What a visitor should leave convinced of, each lit at the moment the run shows it. */
export type Proof = 'agentless' | 'preflight' | 'canary' | 'control' | 'audit' | 'safe';

export const PROOFS: { id: Proof; label: string }[] = [
  { id: 'agentless', label: 'Agentless SSH Automation' },
  { id: 'preflight', label: 'Preflight Validation' },
  { id: 'canary', label: 'Canary Execution' },
  { id: 'control', label: 'Controlled Rollout' },
  { id: 'audit', label: 'Audit Visibility' },
  { id: 'safe', label: 'Safe Multi-Server Operations' },
];

export type LogStage = Stage | 'run' | 'done';

export type Tone = 'plain' | 'ok' | 'wait' | 'note';

export type DemoEvent =
  | { kind: 'log'; stage: LogStage; text: string; tone?: Tone; after?: number }
  | { kind: 'servers'; ids: ServerId[]; state: ServerState }
  | { kind: 'disk'; id: ServerId; disk: number }
  | { kind: 'stage'; stage: Stage; state: StageState }
  | { kind: 'check'; stage: Stage; index: number; state: CheckState }
  | { kind: 'progress'; value: number }
  | { kind: 'proof'; proof: Proof }
  | { kind: 'announce'; text: string }
  | { kind: 'pause'; ms: number };

const ALL: ServerId[] = SERVERS.map((server) => server.id);
const REST: Rest[] = BATCHES.flat();

function serverOf(id: ServerId): DemoServer {
  return SERVERS.find((server) => server.id === id) ?? SERVERS[0];
}

function diskEvent(playbook: DemoPlaybook, id: ServerId): DemoEvent[] {
  return playbook.diskAfter === undefined ? [] : [{ kind: 'disk', id, disk: playbook.diskAfter[id] }];
}

/**
 * Everything up to the confirmation: every server checked, then one server first.
 *
 * The preflight is shown as three sweeps across the fleet, one per check on its card, so a check
 * turns green only when every server has passed it. In a real run the four are checked at once.
 */
export function untilConfirmation(playbook: DemoPlaybook): DemoEvent[] {
  const canary = serverOf(CANARY);

  return [
    { kind: 'log', stage: 'run', text: `${playbook.name} · 4 servers · canary first, then 2 at a time, stopping at the first failure`, tone: 'note', after: 300 },
    { kind: 'log', stage: 'run', text: 'Checking 4 servers before anything is changed.' },
    { kind: 'stage', stage: 'preflight', state: 'running' },
    { kind: 'servers', ids: ALL, state: 'checking' },

    { kind: 'check', stage: 'preflight', index: 0, state: 'running' },
    ...SERVERS.map((s): DemoEvent => ({ kind: 'log', stage: 'preflight', text: `${s.name} · connected over SSH · host key verified` })),
    { kind: 'check', stage: 'preflight', index: 0, state: 'success' },
    { kind: 'proof', proof: 'agentless' },

    { kind: 'check', stage: 'preflight', index: 1, state: 'running' },
    ...SERVERS.map((s): DemoEvent => ({ kind: 'log', stage: 'preflight', text: `${s.name} · ${s.os} detected · supported` })),
    { kind: 'check', stage: 'preflight', index: 1, state: 'success' },

    { kind: 'check', stage: 'preflight', index: 2, state: 'running' },
    ...SERVERS.map((s): DemoEvent => ({
      kind: 'log',
      stage: 'preflight',
      text: `${s.name} · package manager ${s.packageManager} · ${s.root === 'root' ? 'signed in as root' : 'root through sudo'}`,
    })),
    { kind: 'check', stage: 'preflight', index: 2, state: 'success' },

    { kind: 'log', stage: 'preflight', text: 'Preflight passed on 4 of 4 servers. Nothing has been changed yet.', tone: 'ok', after: 350 },
    { kind: 'stage', stage: 'preflight', state: 'success' },
    { kind: 'servers', ids: ALL, state: 'waiting' },
    { kind: 'proof', proof: 'preflight' },
    { kind: 'announce', text: 'Preflight passed on all 4 servers. Nothing has been changed yet.' },

    { kind: 'stage', stage: 'canary', state: 'running' },
    { kind: 'check', stage: 'canary', index: 0, state: 'running' },
    { kind: 'servers', ids: [CANARY], state: 'canary' },
    { kind: 'log', stage: 'canary', text: `Trying ${canary.name} first, as the canary. The other 3 wait.`, after: 300 },
    ...playbook.canary.work.map((text): DemoEvent => ({ kind: 'log', stage: 'canary', text })),
    { kind: 'check', stage: 'canary', index: 0, state: 'success' },

    { kind: 'check', stage: 'canary', index: 1, state: 'running' },
    ...playbook.canary.validate.map((text): DemoEvent => ({ kind: 'log', stage: 'canary', text, tone: 'ok' })),
    ...diskEvent(playbook, CANARY),
    { kind: 'check', stage: 'canary', index: 1, state: 'success' },
    { kind: 'servers', ids: [CANARY], state: 'success' },
    { kind: 'progress', value: 25 },
    { kind: 'proof', proof: 'canary' },

    { kind: 'check', stage: 'canary', index: 2, state: 'running' },
    { kind: 'stage', stage: 'canary', state: 'waiting' },
    { kind: 'log', stage: 'canary', text: `The canary server, ${canary.name}, succeeded. Waiting for you to confirm the remaining 3 servers.`, tone: 'wait' },
    { kind: 'announce', text: `The canary server, ${canary.name}, succeeded. The run is waiting for you to confirm the remaining 3 servers.` },
  ];
}

/** Confirmed: the rest, a batch at a time. */
export function afterConfirmation(playbook: DemoPlaybook): DemoEvent[] {
  const events: DemoEvent[] = [
    { kind: 'check', stage: 'canary', index: 2, state: 'success' },
    { kind: 'stage', stage: 'canary', state: 'success' },
    { kind: 'proof', proof: 'control' },
    { kind: 'stage', stage: 'rollout', state: 'running' },
    { kind: 'log', stage: 'rollout', text: 'Confirmed by you. Continuing on the remaining 3 servers, 2 at a time.', after: 200 },
  ];

  let finished = 1;

  BATCHES.forEach((batch, index) => {
    events.push(
      { kind: 'check', stage: 'rollout', index, state: 'running' },
      { kind: 'log', stage: 'rollout', text: `Batch ${index + 1} of ${BATCHES.length}: ${batch.map((id) => serverOf(id).name).join(', ')}` },
      { kind: 'servers', ids: batch, state: 'working' },
      { kind: 'pause', ms: 500 },
    );

    for (const id of batch) {
      finished += 1;
      events.push(
        { kind: 'log', stage: 'rollout', text: playbook.rollout[id] },
        ...diskEvent(playbook, id),
        { kind: 'servers', ids: [id], state: 'success' },
        { kind: 'progress', value: Math.round((finished / SERVERS.length) * 100) },
      );
    }

    events.push({ kind: 'check', stage: 'rollout', index, state: 'success' });
  });

  events.push(
    { kind: 'check', stage: 'rollout', index: 2, state: 'success' },
    { kind: 'stage', stage: 'rollout', state: 'success' },
    { kind: 'proof', proof: 'safe' },
    { kind: 'log', stage: 'done', text: 'Finished: 4 of 4 servers succeeded, 0 failed.', tone: 'ok', after: 300 },
    { kind: 'log', stage: 'done', text: 'Every server’s output and exit code is kept in run history.', tone: 'note' },
    { kind: 'proof', proof: 'audit' },
    { kind: 'announce', text: `Run finished: 4 servers ${playbook.done.toLowerCase()}, no failures.` },
  );

  return events;
}

/** Stopped at the confirmation: the canary stands, and the rest were never started. */
export function stoppedAtConfirmation(): DemoEvent[] {
  return [
    { kind: 'check', stage: 'canary', index: 2, state: 'success' },
    { kind: 'stage', stage: 'canary', state: 'success' },
    { kind: 'proof', proof: 'control' },
    { kind: 'stage', stage: 'rollout', state: 'skipped' },
    { kind: 'check', stage: 'rollout', index: 0, state: 'skipped' },
    { kind: 'check', stage: 'rollout', index: 1, state: 'skipped' },
    { kind: 'check', stage: 'rollout', index: 2, state: 'skipped' },
    { kind: 'servers', ids: REST, state: 'untouched' },
    { kind: 'log', stage: 'rollout', text: 'Stopped by you after the canary.', tone: 'wait', after: 200 },
    { kind: 'log', stage: 'rollout', text: `Never started, so never changed: ${REST.map((id) => serverOf(id).name).join(', ')}`, tone: 'note' },
    { kind: 'proof', proof: 'safe' },
    { kind: 'log', stage: 'done', text: 'Finished: 1 server changed, 3 not touched, 0 failed. Kept in run history.', tone: 'ok', after: 300 },
    { kind: 'proof', proof: 'audit' },
    { kind: 'announce', text: 'Run stopped after the canary. The other 3 servers were not touched.' },
  ];
}

export function playbookById(id: PlaybookId): DemoPlaybook {
  return PLAYBOOKS.find((playbook) => playbook.id === id) ?? PLAYBOOKS[0];
}
